import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, TrendingUp, X, Search, GripVertical, BarChart3 } from 'lucide-react';
import { statsService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function StatsPage() {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    label: '',
    value: '',
    suffix: '',
    icon: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    filterStats();
  }, [stats, searchQuery, filterStatus]);

  const filterStats = () => {
    let filtered = [...stats];

    // Recherche par label
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(stat =>
        stat.label.toLowerCase().includes(query)
      );
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(stat =>
        filterStatus === 'active' ? stat.is_active : !stat.is_active
      );
    }

    setFilteredStats(filtered);
  };

const loadStats = async () => {
  try {
    // Utiliser getAllAdmin pour avoir toutes les stats (actives et inactives)
    const response = await statsService.getAllAdmin();
    console.log('Stats chargées:', response.data);
    const statsData = response.data.stats || response.data || [];
    setStats(Array.isArray(statsData) ? statsData : []);
  } catch (error) {
    console.error('Erreur:', error);
    // Fallback sur getAll si getAllAdmin échoue
    try {
      const response = await statsService.getAll();
      const statsData = response.data.stats || response.data || [];
      setStats(Array.isArray(statsData) ? statsData : []);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    }
  } finally {
    setLoading(false);
  }
};

  const openModal = (stat = null) => {
    if (stat) {
      setEditing(stat);
      setFormData({
        label: stat.label || '',
        value: stat.value || '',
        suffix: stat.suffix || '',
        icon: stat.icon || '',
        display_order: stat.display_order || 0,
        is_active: stat.is_active ? true : false
      });
    } else {
      setEditing(null);
      setFormData({
        label: '',
        value: '',
        suffix: '',
        icon: '',
        display_order: stats.length + 1,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label || !formData.value) {
      toast.error('Label et valeur sont requis');
      return;
    }

    try {
      if (editing) {
        await statsService.update(editing.id, formData);
        toast.success('Statistique mise à jour');
      } else {
        await statsService.create(formData);
        toast.success('Statistique créée');
      }
      setShowModal(false);
      loadStats();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette statistique ?')) return;

    try {
      await statsService.delete(id);
      toast.success('Statistique supprimée');
      loadStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (stat) => {
    try {
      await statsService.update(stat.id, { is_active: !stat.is_active });
      toast.success('Statut mis à jour');
      loadStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
  };

  // Icônes prédéfinies pour les stats
  const iconOptions = [
    { value: '', label: 'Aucune' },
    { value: 'calendar', label: '📅 Calendrier' },
    { value: 'briefcase', label: '💼 Projets' },
    { value: 'users', label: '👥 Utilisateurs' },
    { value: 'check', label: '✅ Succès' },
    { value: 'star', label: '⭐ Étoile' },
    { value: 'trophy', label: '🏆 Trophée' },
    { value: 'chart', label: '📊 Graphique' },
    { value: 'globe', label: '🌍 Globe' },
    { value: 'building', label: '🏢 Entreprise' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Statistiques</h1>
          <p className="text-dark-500">Gérez les chiffres clés affichés sur le site</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouvelle statistique
        </button>
      </div>

      {/* Aperçu en temps réel */}
      <div className="card p-6 mb-6 bg-gradient-to-r from-dark-900 via-dark-800 to-primary-900">
        <h3 className="text-white text-sm font-medium mb-4 opacity-70">Aperçu sur le site</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.filter(s => s.is_active).map((stat) => (
            <div key={stat.id} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {stat.value}
                {stat.suffix && <span className="text-primary-400">{stat.suffix}</span>}
              </div>
              <p className="text-dark-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Recherche */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par label..."
                className="input pl-10"
              />
            </div>
          </div>

          {/* Filtre Statut */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>

        {/* Résumé */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-100">
          <div className="flex items-center gap-4">
            {(searchQuery || filterStatus !== 'all') && (
              <button
                onClick={resetFilters}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
          <div className="text-sm text-dark-500">
            {filteredStats.length} statistique{filteredStats.length > 1 ? 's' : ''} affichée{filteredStats.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Liste des statistiques */}
      {filteredStats.length === 0 ? (
        <div className="card p-12 text-center">
          <BarChart3 className="w-16 h-16 text-dark-300 mx-auto mb-4" />
          <p className="text-dark-500 mb-4">
            {stats.length === 0
              ? 'Aucune statistique'
              : 'Aucun résultat pour ces critères'}
          </p>
          {stats.length === 0 ? (
            <button onClick={() => openModal()} className="btn-primary">
              Ajouter une statistique
            </button>
          ) : (
            <button onClick={resetFilters} className="btn-ghost">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredStats.map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card p-6 relative group"
            >
              {/* Actions */}
              <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openModal(stat)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-dark-50"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4 text-dark-600" />
                </button>
                <button
                  onClick={() => handleDelete(stat.id)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-red-50"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>

              {/* Ordre d'affichage */}
              <div className="absolute top-4 left-4">
                <span className="flex items-center gap-1 text-xs text-dark-400 bg-dark-100 px-2 py-1 rounded">
                  <GripVertical className="w-3 h-3" />
                  #{stat.display_order}
                </span>
              </div>

              {/* Contenu */}
              <div className="text-center mt-6">
                <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-7 h-7 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-dark-900 mb-1">
                  {stat.value}
                  {stat.suffix && <span className="text-primary-600">{stat.suffix}</span>}
                </div>
                <p className="text-dark-500 text-sm">{stat.label}</p>
              </div>

              {/* Status */}
              <div className="mt-4 pt-4 border-t border-dark-100 flex items-center justify-center">
                <button
                  onClick={() => toggleActive(stat)}
                  className={`badge cursor-pointer ${stat.is_active ? 'badge-secondary' : 'bg-dark-100 text-dark-500'}`}
                >
                  {stat.is_active ? 'Actif' : 'Inactif'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">
                {editing ? 'Modifier la statistique' : 'Nouvelle statistique'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Label */}
              <div>
                <label className="label">Label *</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                  className="input"
                  required
                  placeholder="Ex: Années d'expérience"
                />
              </div>

              {/* Valeur et Suffixe */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Valeur *</label>
                  <input
                    type="text"
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                    className="input"
                    required
                    placeholder="Ex: 15"
                  />
                </div>
                <div>
                  <label className="label">Suffixe</label>
                  <input
                    type="text"
                    value={formData.suffix}
                    onChange={(e) => setFormData(prev => ({ ...prev, suffix: e.target.value }))}
                    className="input"
                    placeholder="Ex: + ou %"
                  />
                  <p className="text-xs text-dark-400 mt-1">Symbole après la valeur (+, %, etc.)</p>
                </div>
              </div>

              {/* Aperçu */}
              <div className="p-4 bg-dark-900 rounded-xl text-center">
                <p className="text-xs text-dark-400 mb-2">Aperçu</p>
                <div className="text-3xl font-bold text-white">
                  {formData.value || '0'}
                  {formData.suffix && <span className="text-primary-400">{formData.suffix}</span>}
                </div>
                <p className="text-dark-400 text-sm mt-1">{formData.label || 'Label'}</p>
              </div>

              {/* Icône */}
              <div>
                <label className="label">Icône</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                  className="input"
                >
                  {iconOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ordre d'affichage */}
              <div>
                <label className="label">Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="input"
                  min="0"
                />
                <p className="text-xs text-dark-400 mt-1">Les statistiques sont triées par ordre croissant</p>
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-dark-700">
                  Statistique active (visible sur le site)
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default StatsPage;